import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    RiHome4Line,
    RiBookOpenLine,
    RiNewspaperLine,
    RiPlantLine,
    RiCompass3Line,
    RiArrowRightLine,
    RiTeamLine,
    RiMailLine,
    RiMenu4Line,
    RiCloseLine,
    RiLeafLine,
    RiArrowDownSLine
} from 'react-icons/ri';

const NavLink = ({ label, icon: Icon, hasDropdown, custom, subItems, path }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <motion.div
            custom={custom}
            variants={{
                hidden: { opacity: 0, y: -10 },
                visible: (i) => ({
                    opacity: 1,
                    y: 0,
                    transition: { delay: i * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] }
                })
            }}
            onMouseEnter={() => setIsOpen(true)}
            onMouseLeave={() => setIsOpen(false)}
        >
            <Link
                to={path}
                className="nav-link-v2 flex flex-col items-center justify-center gap-1 px-4 py-3 hover:bg-moss/5 rounded-2xl transition-all duration-300 relative min-w-[90px]"
            >
                <motion.span
                    whileHover={{ scale: 1.2, rotate: 10 }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    className="text-moss/60 group-hover:text-moss transition-colors"
                >
                    {Icon && <Icon size={20} />}
                </motion.span>
                <span className="text-[11px] font-black uppercase tracking-widest font-display">{label}</span>
                {hasDropdown && (
                    <motion.span
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                        className="absolute bottom-1 right-1"
                    >
                        <RiArrowDownSLine className="text-[12px] text-moss/30 group-hover:text-moss/70" />
                    </motion.span>
                )}
            </Link>

            {hasDropdown && (
                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            initial={{ opacity: 0, y: 10, scale: 0.95, x: '-50%' }}
                            animate={{ opacity: 1, y: 0, scale: 1, x: '-50%' }}
                            exit={{ opacity: 0, y: 10, scale: 0.95, x: '-50%' }}
                            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                            className="absolute top-full left-1/2 mt-5 w-80 bg-white backdrop-blur-3xl border border-white/60 rounded-[20px] shadow-ultra-premium p-5 z-50 overflow-hidden ring-1 ring-black/5"
                        >
                            {/* Accent Glow */}
                            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-emerald-400/40 to-transparent" />

                            <div className="px-6 pb-4 mb-4 border-b border-moss/10 flex items-center justify-between">
                                <span className="text-[10px] font-black text-moss/40 uppercase tracking-[0.4em] font-display">{label} Archives</span>
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            </div>

                            <div className="grid grid-cols-1 gap-1.5">
                                {subItems?.map((item, i) => (
                                    <motion.div
                                        key={item.label}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: i * 0.05 + 0.1 }}
                                    >
                                        <Link
                                            to={item.path}
                                            className="flex items-center gap-4 px-5 py-4 text-[14px] font-bold text-slate-800 hover:text-emerald-700 hover:bg-emerald-500/5 rounded-3xl transition-all duration-500 group/sub"
                                            onClick={() => setIsOpen(false)}
                                        >
                                            <div className="w-10 h-10 rounded-2xl bg-white/80 shadow-sm flex items-center justify-center text-moss group-hover/sub:bg-emerald-600 group-hover/sub:text-white group-hover/sub:scale-110 transition-all duration-500">
                                                <item.icon size={18} />
                                            </div>
                                            <div className="flex flex-col gap-0.5 font-sans">
                                                <span className="leading-none text-[14px] font-bold text-slate-800 group-hover/sub:text-emerald-700">{item.label}</span>
                                                <span className="text-[9px] font-black text-slate-400/80 uppercase tracking-widest">{item.desc}</span>
                                            </div>
                                        </Link>
                                    </motion.div>
                                ))}
                            </div>

                            {/* Decorative footer element */}
                            <div className="mt-4 pt-4 border-t border-moss/5 flex justify-center">
                                <span className="text-[8px] font-black text-moss/20 uppercase tracking-[0.5em]">Central Observatory of Ecology</span>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            )}
        </motion.div >
    );
};

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const menuItems = [
        { label: 'Home', icon: RiHome4Line, path: '/' },
        {
            label: 'Publications',
            icon: RiBookOpenLine,
            hasDropdown: true,
            path: '/publications',
            subItems: [
                { label: 'Featured Studies', path: '/publications', icon: RiBookOpenLine, desc: 'Major breakthroughs' },
                { label: 'Botanical Archives', path: '/publications', icon: RiPlantLine, desc: 'Detailed records' },
                { label: 'Research Papers', path: '/publications', icon: RiNewspaperLine, desc: 'Peer-reviewed' }
            ]
        },
        { label: 'News', icon: RiNewspaperLine, path: '/news' },
        {
            label: 'Flora and Fauna',
            icon: RiPlantLine,
            hasDropdown: true,
            path: '/flora-fauna',
            subItems: [
                { label: 'Species Catalog', path: '/flora-fauna', icon: RiPlantLine, desc: 'Living database' },
                { label: 'Habitat Mapping', path: '/flora-fauna', icon: RiCompass3Line, desc: 'Global zones' },
                { label: 'Conservation', path: '/flora-fauna', icon: RiLeafLine, desc: 'Status reports' }
            ]
        },
        { label: 'Travel', icon: RiCompass3Line, path: '/travel' },
        { label: 'About us', icon: RiTeamLine, path: '/about' },
        { label: 'Contact us', icon: RiMailLine, path: '/contact' }
    ];

    return (
        <div className="fixed top-0 left-0 right-0 z-50 px-0 sm:px-6 lg:px-10 py-4 sm:py-6 transition-all duration-700">
            <motion.nav
                initial="hidden"
                animate="visible"
                className={`mx-auto max-w-7xl bg-white backdrop-blur-2xl rounded-full border border-white/60 px-4 sm:px-8 py-3 flex items-center justify-between transition-all duration-700 ${scrolled ? 'shadow-2xl py-2 bg-white/70 scale-[0.97]' : 'shadow-xl py-4'}`}
            >

                {/* Left: Architectural Branding */}
                <motion.div
                    variants={{
                        hidden: { opacity: 0, x: -25 },
                        visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
                    }}
                    className="flex items-center gap-4 cursor-pointer group"
                >
                    <motion.div
                        whileHover={{ scale: 1.1, rotate: -5 }}
                        className="  p-2 rounded-2xl relative overflow-hidden transition-all duration-700   flex items-center justify-center w-32"
                    >
                        <img
                            src="/images/mlogo.png"
                            alt="COE Logo"
                            className="w-full h-full object-contain relative z-10"
                        />
                        <motion.div
                            animate={{ rotate: 360, scale: [1, 1.2, 1] }}
                            transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                            className="absolute -inset-2 opacity-30 bg-gradient-to-tr from-moss to-transparent rounded-full"
                        />
                    </motion.div>

                </motion.div>

                {/* Center: Minimalist Link Stack */}
                <div className="hidden xl:flex items-center gap-1">
                    {menuItems.map((item, i) => (
                        <NavLink key={item.label} {...item} custom={i} />
                    ))}
                </div>

                {/* Right: Glass Actions */}
                <div className="flex items-center gap-2 sm:gap-4">
                    <motion.button
                        variants={{
                            hidden: { opacity: 0, x: 25 },
                            visible: { opacity: 1, x: 0, transition: { delay: 0.6 } }
                        }}
                        whileHover={{ scale: 1.05, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn-ultra-modern whitespace-nowrap group text-xs sm:text-sm px-4 sm:px-6 py-2 sm:py-3"
                    >
                        <div className="btn-ultra-modern-core" />
                        <span className="hidden sm:inline">Join Legacy</span>
                        <span className="inline sm:hidden">Join</span>
                        <motion.div
                            whileHover={{ x: 3 }}
                            className="bg-white/10 p-1 rounded-full group-hover:bg-white/20 transition-colors ml-2"
                        >
                            <RiArrowRightLine size={12} className="text-natural" />
                        </motion.div>
                    </motion.button>

                    {/* Mobile Menu Toggle */}
                    <motion.button
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className="xl:hidden p-2 text-moss hover:bg-moss/5 rounded-full transition-colors"
                        whileTap={{ scale: 0.9 }}
                    >
                        {isMobileMenuOpen ? <RiCloseLine size={24} /> : <RiMenu4Line size={24} />}
                    </motion.button>
                </div>
            </motion.nav>

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="xl:hidden mx-auto max-w-7xl mt-4 bg-white/95 backdrop-blur-2xl rounded-[32px] border border-white/60 shadow-2xl overflow-hidden z-40"
                    >
                        <div className="p-6 space-y-2">
                            {menuItems.map((item, i) => (
                                <motion.div
                                    key={item.label}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: i * 0.1 }}
                                >
                                    <Link
                                        to={item.path}
                                        className="flex items-center gap-4 p-4 hover:bg-moss/5 rounded-2xl transition-colors group"
                                        onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                        <div className="w-10 h-10 rounded-xl bg-moss/5 flex items-center justify-center text-moss group-hover:bg-moss group-hover:text-white transition-all duration-500">
                                            <item.icon size={20} />
                                        </div>
                                        <span className="font-black text-slate-900 uppercase tracking-widest text-xs font-display">{item.label}</span>
                                    </Link>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Navbar;
